import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { Bug, X, PaperPlaneTilt, Camera, CircleNotch, CheckCircle } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { submitBugReport } from "@/lib/api";
import { getLogs } from "@/lib/logbuffer";

export function BugReportButton() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const [description, setDescription] = useState("");
  const [shot, setShot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const openReporter = async () => {
    setOpen(true);
    setSent(false);
    setDescription("");
    setShot(null);
    // capture the current page as evidence before the dialog paints over it
    setCapturing(true);
    try {
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale: Math.min(1, window.devicePixelRatio || 1),
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
      });
      setShot(canvas.toDataURL("image/jpeg", 0.6));
    } catch (_) {
      setShot(null);
    } finally {
      setCapturing(false);
    }
  };

  const send = async () => {
    if (!description.trim()) {
      toast.error("Please describe what went wrong");
      return;
    }
    setSending(true);
    try {
      await submitBugReport({
        description,
        url: window.location.href,
        user_agent: navigator.userAgent,
        console_logs: getLogs(),
        screenshot: shot || "",
        app: "SpeakToPrompt",
      });
      setSent(true);
      toast.success("Bug report sent — thank you!");
      setTimeout(() => setOpen(false), 1400);
    } catch (e) {
      toast.error("Could not send report. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="bug-report-fab"
        onClick={openReporter}
        title="Report a bug"
        data-html2canvas-ignore="true" className={`stp-glass fixed right-3 z-40 flex h-10 w-10 items-center justify-center gap-2 rounded-full opacity-80 sm:bottom-5 sm:right-5 sm:h-11 sm:w-auto sm:px-4 sm:opacity-100 ${pathname === "/app" ? "bottom-[calc(68px+env(safe-area-inset-bottom))]" : "bottom-[calc(12px+env(safe-area-inset-bottom))]"} text-sm font-medium text-ink transition-transform hover:scale-[1.03] active:scale-95`}
      >
        <Bug size={18} weight="duotone" /> <span className="hidden sm:inline">Report a bug</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-html2canvas-ignore="true" className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-4 backdrop-blur-sm sm:items-center"
            onClick={() => !sending && setOpen(false)}
            data-testid="bug-report-modal"
          >
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ type: "spring", stiffness: 300, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl sm:p-8"
            >
              <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ink">
                  <Bug size={20} weight="duotone" color="#FF3B30" /> Report a bug
                </h2>
                <button data-testid="bug-close-button" onClick={() => !sending && setOpen(false)} className="text-mist hover:text-ink">
                  <X size={18} weight="bold" />
                </button>
              </div>

              {sent ? (
                <div className="flex flex-col items-center py-8 text-center" data-testid="bug-report-success">
                  <CheckCircle size={48} weight="fill" className="text-[#34C759]" />
                  <p className="mt-3 font-display text-xl font-semibold text-ink">Report sent!</p>
                  <p className="mt-1 text-sm text-mist">Our team will look into it. Thank you.</p>
                </div>
              ) : (
                <>
                  <p className="mb-3 text-sm text-mist">
                    Describe what happened. We automatically attach a screenshot, the page URL and recent error logs as evidence.
                  </p>
                  <Textarea
                    data-testid="bug-description-input"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="e.g. I clicked Generate and the output stayed blank..."
                    className="min-h-[110px] resize-none rounded-2xl border-0 bg-[#FAFAFC] text-sm text-ink placeholder:text-mist/70 focus-visible:ring-2 focus-visible:ring-brand/30"
                  />

                  <div className="mt-4 flex items-center gap-3 rounded-2xl bg-[#FAFAFC] p-3">
                    <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-black/5 bg-white">
                      {capturing ? (
                        <CircleNotch size={20} className="animate-spin text-mist" />
                      ) : shot ? (
                        <img src={shot} alt="evidence" className="h-full w-full object-cover" data-testid="bug-screenshot-preview" />
                      ) : (
                        <Camera size={20} className="text-mist" />
                      )}
                    </div>
                    <div className="text-xs text-mist">
                      <p className="font-medium text-ink">Evidence attached</p>
                      <p>{capturing ? "Capturing screenshot..." : shot ? "Screenshot + URL + error logs" : "Screenshot unavailable — URL & logs still attached"}</p>
                    </div>
                  </div>

                  <div className="mt-5 flex justify-end gap-2">
                    <Button variant="ghost" data-testid="bug-cancel-button" onClick={() => setOpen(false)} disabled={sending} className="rounded-full bg-black/5 text-ink hover:bg-black/10">
                      Cancel
                    </Button>
                    <Button
                      data-testid="bug-submit-button"
                      onClick={send}
                      disabled={sending || capturing}
                      className="gap-2 rounded-full bg-ink font-semibold text-white hover:bg-black"
                    >
                      {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />}
                      {sending ? "Sending..." : "Send report"}
                    </Button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
